import { SingleEntitySelector } from "@/components/SingleEntitySelector";
import { MultiEntitySelector } from "@/components/MultiEntitySelector";
import { EntitySelectorBaseProps } from "@/components/EntitySelectorBase";

export type { EntitySelectorBaseProps } from "@/components/EntitySelectorBase";

type SingleEntitySelectorProps<T extends { id: string }> =
  EntitySelectorBaseProps<T> & {
    mode?: "single";
    currentlySelectedId: string | null;
    onSelectId: (id: string | null) => void;
  };

type MultiEntitySelectorProps<T extends { id: string }> =
  EntitySelectorBaseProps<T> & {
    mode: "multi";
    selectedIds: string[];
    onSelectedIdsChange: (ids: string[]) => void;
  };

type EntitySelectorProps<T extends { id: string }> =
  | SingleEntitySelectorProps<T>
  | MultiEntitySelectorProps<T>;

/**
 * Dispatches to the single or multi entity selector based on the mode prop.
 * @param props - The shared selector props plus the mode-specific selection props
 * @returns The selector for the requested mode
 */
export function EntitySelector<T extends { id: string }>(
  props: EntitySelectorProps<T>,
) {
  if (props.mode === "multi") {
    const { mode, selectedIds, onSelectedIdsChange, ...baseProps } = props;
    return (
      <MultiEntitySelector<T>
        {...baseProps}
        selectedIds={selectedIds}
        onSelectedIdsChange={onSelectedIdsChange}
      />
    );
  }

  const { mode, currentlySelectedId, onSelectId, ...baseProps } = props;
  return (
    <SingleEntitySelector<T>
      {...baseProps}
      currentlySelectedId={currentlySelectedId}
      onSelectId={onSelectId}
    />
  );
}
